import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { settingsAPI, tasksAPI } from '../lib/api';
import { Users, Settings, FileText, ClipboardList, MessageSquareDot, LayoutDashboard, UtensilsCrossed, GraduationCap, CalendarCheck, CalendarDays, FileSignature, FileDown, Mail, X, Menu, LogOut, Layers } from 'lucide-react';
import '../ica-theme.css';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [taskBadge, setTaskBadge] = useState(0);

  useEffect(() => {
    settingsAPI.get().then(res => {
      if (res.data.logo_url) setLogoUrl(res.data.logo_url);
    }).catch(() => {});
    const refreshBadge = () => {
      tasksAPI.buckets().then((res) => {
        const overdue = (res.data?.overdue || []).length;
        const today = (res.data?.today || []).length;
        setTaskBadge(overdue + today);
      }).catch(() => {});
    };
    refreshBadge();
    const t = setInterval(refreshBadge, 60000); // safety poll every minute
    const handleTaskChange = () => refreshBadge();
    window.addEventListener('tasks-changed', handleTaskChange);
    return () => {
      clearInterval(t);
      window.removeEventListener('tasks-changed', handleTaskChange);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: () => <LayoutDashboard className="w-5 h-5 mr-3" /> },
    { path: '/restaurants', label: 'Restaurants', icon: () => <UtensilsCrossed className="w-5 h-5 mr-3" /> },
    { path: '/students', label: 'Students', icon: () => <GraduationCap className="w-5 h-5 mr-3" /> },
    { path: '/cycles', label: 'Placement Cycles', icon: () => <Layers className="w-5 h-5 mr-3" /> },
    { path: '/compliance', label: 'Compliance', icon: () => <FileSignature className="w-5 h-5 mr-3" /> },
    { path: '/placement-feedback', label: 'Placement Feedback', icon: () => <MessageSquareDot className="w-5 h-5 mr-3" /> },
    { path: '/student-records', label: 'Student Records', icon: () => <FileText className="w-5 h-5 mr-3" /> },
    { path: '/placement-calendar', label: 'Tasks', icon: () => <CalendarDays className="w-5 h-5 mr-3" />, badge: taskBadge },
  ];

  // Admin-only nav items
  const adminNavItems = [
    { path: '/feedback-templates', label: 'Feedback Forms', icon: () => <ClipboardList className="w-5 h-5 mr-3" /> },
    { path: '/assessment-rules', label: 'Assessment Rules', icon: () => <ClipboardList className="w-5 h-5 mr-3" /> },
    { path: '/contract-templates', label: 'Contract Templates', icon: () => <FileSignature className="w-5 h-5 mr-3" /> },
    { path: '/cycle-exports', label: 'Cycle Exports', icon: () => <FileDown className="w-5 h-5 mr-3" /> },
    { path: '/email-templates', label: 'Email Templates', icon: () => <Mail className="w-5 h-5 mr-3" /> },
    { path: '/users', label: 'Users', icon: () => <Users className="w-5 h-5 mr-3" /> },
    { path: '/settings', label: 'Settings', icon: () => <Settings className="w-5 h-5 mr-3" /> },
  ];

  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-btn"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-ica-navy text-white rounded-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside
        className={`sidebar ${sidebarOpen ? 'open' : ''} lg:translate-x-0 bg-white border-r border-gray-200`}
        style={{ width: '256px', background: '#ffffff' }}
      >
        {/* Logo */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-center">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="w-12 h-12 object-contain" />
            ) : (
              <svg width="42" height="42" viewBox="0 0 24 24" fill="none" className="text-ica-gold">
                <path d="M12 2C12 2 12 6 12 8C12 10 14 12 14 12C14 12 12 14 12 16C12 18 12 22 12 22" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 6C8 6 10 8 12 8C14 8 16 6 16 6" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M6 12C6 12 9 10 12 10C15 10 18 12 18 12" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 18C8 18 10 16 12 16C14 16 16 18 16 18" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            )}
          </div>
          <div className="mt-3 text-center">
            <p className="font-semibold text-ica-navy">{user?.name}</p>
            <p className="text-gray-500 text-sm capitalize">{user?.role}</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {user?.role === 'support' && (
            <div
              data-testid="readonly-banner"
              className="mx-4 mb-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800"
            >
              <div className="font-semibold uppercase tracking-wider text-amber-700">Read-only access</div>
              <div className="mt-0.5">Support Staff — you can view every placement, restaurant and document but cannot make changes.</div>
            </div>
          )}
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center px-6 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-ica-navy/5 text-ica-navy border-l-4 border-ica-gold'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-ica-navy border-l-4 border-transparent'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon />
              <span className="flex-1">{item.label}</span>
              {item.badge ? (
                <span
                  data-testid={`nav-badge-${item.label.toLowerCase()}`}
                  className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold"
                >
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              ) : null}
            </NavLink>
          ))}

          {/* Admin Section */}
          {user?.role === 'admin' && (
            <>
              <div className="px-6 py-3 mt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</p>
              </div>
              {adminNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center px-6 py-3 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-ica-navy/5 text-ica-navy border-l-4 border-ica-gold'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-ica-navy border-l-4 border-transparent'
                    }`
                  }
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-gray-100">
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex items-center px-2 py-2 text-sm text-gray-600 hover:text-red-600 transition-colors w-full"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content lg:ml-64">
        {children}
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
